import {
  BodyText,
  Button,
  Callout,
  DatePicker,
  Field,
  FieldLabel,
  RadioGroup,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { formatUtcDateTime } from "@/lib/time";
import {
  useCreateGenericObservation,
  useGetGenericObservations,
} from "@/network/useGenericObservations";
import type {
  CreateGenericObservationRequest,
  ObservationGroupItem,
} from "@/types/observations";
import { GroupItemAnswerType } from "@/types/observations";
import { Observation } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CirclePlus, EllipsisVertical } from "lucide-react";
import { Fragment, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { getObservationDate } from "../utils";
import {
  type HealthMaintenanceFormData,
  healthMaintenanceFormSchema,
} from "./schemas";

interface HealthScreeningSectionProps {
  patientId: string;
  groupIdentifier: string;
}

const getDisplayName = (friendlyDisplay: string): string => {
  // Remove "Date of last" prefix if present for cleaner display
  const cleaned = friendlyDisplay.replace(/^Date of last /i, "");
  // Apply title case to each word in the cleaned name
  return cleaned
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getFieldStatus = (
  item: ObservationGroupItem,
): { text: string; isDeclined: boolean } => {
  if (!item.documentedObservation) {
    return { text: "Unknown", isDeclined: false };
  }

  const observation = new Observation(item.documentedObservation);
  const observationDate = getObservationDate(observation);
  const date = observationDate
    ? format(new Date(observationDate), "MM/dd/yy")
    : "";

  // Check if declined using DataAbsentReason
  if (
    observation.dataAbsentReasonCode ===
    Observation.DataAbsentReason.values.NOT_PERFORMED
  ) {
    return {
      text: `Declined ${date}`,
      isDeclined: true,
    };
  }

  // Check if completed
  if (observation.status === Observation.Status.FINAL && observationDate) {
    return {
      text: `Completed ${date}`,
      isDeclined: false,
    };
  }

  return { text: "Unknown", isDeclined: false };
};

export const HealthScreeningSection = ({
  patientId,
  groupIdentifier,
}: HealthScreeningSectionProps) => {
  const [editingField, setEditingField] = useState<string | null>(null);

  const form = useForm<HealthMaintenanceFormData>({
    resolver: zodResolver(healthMaintenanceFormSchema),
    defaultValues: {
      entryType: "completed",
      date: new Date(),
    },
  });

  const { mutate: createGenericObservation, isPending: isCreatingObservation } =
    useCreateGenericObservation({
      patientId,
      group: groupIdentifier,
      onSuccess: () => {
        toast.success(`${editingField} entry added`);
        setEditingField(null);
        form.reset();
      },
      onError: (error) => {
        toast.error(
          `Failed to save observation: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const handleEditField = (fieldName: string) => {
    setEditingField(fieldName);
    form.reset();
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    form.reset();
  };

  const handleSave = (data: HealthMaintenanceFormData) => {
    const fieldItem = observationItems?.items.find(
      (item) => item.observationCodeFriendlyDisplay === editingField,
    );

    let payload: CreateGenericObservationRequest;

    // Set the date to noon in its current timezone
    const noonDate = new Date(data.date);
    noonDate.setHours(12, 0, 0, 0);

    // Base payload structure
    const basePayload = {
      patientId,
      category: Observation.Category.EXAM,
      status: Observation.Status.FINAL,
      code: {
        coding: [
          {
            code: fieldItem?.observationCode,
            system: fieldItem?.observationCodeSystem,
            display: fieldItem?.observationCodeDisplay,
          },
        ],
        text: fieldItem?.observationCodeFriendlyDisplay,
      },
      effectiveDateTime: formatUtcDateTime(noonDate.toISOString()),
    };

    // Determine payload based on field type and entry type
    if (fieldItem?.groupItemAnswerType === GroupItemAnswerType.DATE) {
      if (data.entryType === "completed") {
        // Date field + Completed: use date for effective date and valueDateTime, omit dataAbsentReason
        payload = {
          ...basePayload,
          fhirValue: {
            valueDateTime: formatUtcDateTime(noonDate.toISOString()),
          },
        };
      } else {
        // Date field + Declined: use date for effective date, set dataAbsentReason to "not-performed", no value
        payload = {
          ...basePayload,
          dataAbsentReason: Observation.DataAbsentReason.values.NOT_PERFORMED,
        };
      }
    } else {
      // Non-date field
      if (data.entryType === "completed") {
        // Non-date field + Completed: use date for effective date, set dataAbsentReason to "temp-unknown", no value
        payload = {
          ...basePayload,
          dataAbsentReason: Observation.DataAbsentReason.values.TEMP_UNKNOWN,
        };
      } else {
        // Non-date field + Declined: use date for effective date, set dataAbsentReason to "not-performed", no value
        payload = {
          ...basePayload,
          dataAbsentReason: Observation.DataAbsentReason.values.NOT_PERFORMED,
        };
      }
    }

    createGenericObservation(payload);
  };
  const {
    data: observationItems,
    isLoading: isLoadingObservations,
    isError: isObservationError,
    refetch: refetchObservations,
  } = useGetGenericObservations(patientId, groupIdentifier);

  const isDataLoadError =
    !isLoadingObservations && (isObservationError || !observationItems);

  if (isDataLoadError) {
    return (
      <div className="flex flex-col gap-2 py-2 w-full">
        <Callout
          className="w-full cursor-pointer"
          variant="danger"
          title={`Unable to load ${groupIdentifier} data!`}
          description="Please click here to try again."
          onClick={() => refetchObservations()}
        />
        <Separator />
      </div>
    );
  }

  if (isLoadingObservations) {
    return (
      <div className="flex flex-col gap-2 rounded transition-colors mt-1 py-1">
        {[
          `${groupIdentifier}-skeleton-1`,
          `${groupIdentifier}-skeleton-2`,
          `${groupIdentifier}-skeleton-3`,
        ].map((key) => (
          <Fragment key={key}>
            <div className="flex justify-between items-center px-3 py-2">
              <div className="h-4 w-32 bg-tertiary rounded animate-pulse" />
              <div className="h-4 w-24 bg-tertiary rounded animate-pulse" />
            </div>
            <Separator />
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {observationItems?.items.map((item) => {
        const fieldName = item.observationCodeFriendlyDisplay;
        const displayName = getDisplayName(fieldName);
        const statusInfo = getFieldStatus(item);
        const isEditing = editingField === fieldName;

        return (
          <Fragment
            key={`${groupIdentifier}-field-${item.observationCode || fieldName}`}
          >
            {isEditing ? (
              <form
                onSubmit={form.handleSubmit(handleSave)}
                className="flex flex-col gap-3 px-3 py-3"
              >
                <div className="flex gap-6 justify-between">
                  <Controller
                    control={form.control}
                    name="entryType"
                    render={({ field }) => (
                      <Field className="flex flex-col">
                        <FieldLabel htmlFor="entryType">
                          <BodyText size="sm" bold>
                            Entry Type
                          </BodyText>
                        </FieldLabel>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          name={`entry-type-${fieldName}`}
                          options={[
                            { label: "Completed", value: "completed" },
                            { label: "Declined", value: "declined" },
                          ]}
                        />
                      </Field>
                    )}
                  />

                  <Controller
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <Field className="flex flex-col w-1/2">
                        <FieldLabel htmlFor="date">
                          <BodyText size="sm" bold>
                            {form.watch("entryType") === "declined"
                              ? "Date Declined"
                              : `Date of ${displayName}`}
                          </BodyText>
                        </FieldLabel>
                        <DatePicker
                          date={field.value}
                          setDate={field.onChange}
                          label=""
                          closeOnSelect={true}
                          disableFuture={true}
                        />
                      </Field>
                    )}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <SubmitCancelButtons
                    isSaving={isCreatingObservation}
                    onCancel={handleCancelEdit}
                  />
                </div>
              </form>
            ) : (
              <div className="flex justify-between items-center min-h-8 pl-3">
                <BodyText size="sm" className="w-[35%]" title={displayName}>
                  {displayName}
                </BodyText>
                {/* Spacer to push status column to align with questionnaire table */}
                <div className="w-[35%]" />

                {/* Status Column - matches questionnaire table width and position */}
                <div className="w-[30%] flex items-center justify-between gap-2">
                  <div className="flex flex-col gap-1 py-1">
                    <BodyText
                      size="sm"
                      textColor={
                        statusInfo.text === "Unknown"
                          ? "tertiary"
                          : statusInfo.isDeclined
                            ? "warning"
                            : "primary"
                      }
                    >
                      {statusInfo.text}
                    </BodyText>
                  </div>

                  <DropdownMenu>
                    <Tooltip
                      tooltipContentText="Options"
                      side="bottom"
                      triggerClassName="leading-[0px]"
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="icon"
                          size="sm"
                          aria-label={`Options for ${displayName}`}
                          className="m-0 p-0 h-4"
                        >
                          <EllipsisVertical className="text-link" size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                    </Tooltip>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem
                        onClick={() => handleEditField(fieldName)}
                      >
                        <CirclePlus className="h-4 w-4 mr-2" />
                        New Entry
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
            <Separator />
          </Fragment>
        );
      })}
    </div>
  );
};
